import isEmpty from 'lodash/isEmpty';
import { nanoid } from 'nanoid';

import { ContentMetadata } from '@candu-ai/content/types';
import { BuiltInComponents } from 'craft/constants';
import { CONDITIONAL_ID } from 'craft/containers/Conditionals/useNodeConditional';
import { dismissLabelAdapter } from 'craft/containers/Craft/format/dismissAdapter';
import { modalAdapters } from 'craft/containers/Craft/format/modalAdapters';
import { ContentNode, CssSelectorDefinitions, CssSelectorId, ThenType } from 'craft/types';
import { SegmentDefinition } from 'dashboard/pages/Segment/components';

const hasEventOccurred = (eventName: string): SegmentDefinition => ({
  eventName,
  operation: {
    operand: '1970-01-01T00:00:00.000Z',
    type: '>=',
  },
  trackingEventType: 'Global',
  type: 'EventOccurred',
  // @ts-expect-error
  object: 'Single',
});

const formAdapter = (selector: CssSelectorId) => (node: ContentNode): ContentNode => {
  const { inputStyles, ...otherCustom } = node.data?.custom ?? {};

  if (isEmpty(inputStyles)) {
    return node;
  }
  // @ts-ignore
  const newSelector = { ...CssSelectorDefinitions[selector], css: inputStyles };
  return {
    ...node,
    data: { ...node.data, custom: otherCustom },
    cssSelectors: { ...(node.cssSelectors ?? {}), [selector]: newSelector },
  };
};

const adapters: Record<
  string,
  (node: ContentNode, nodeId: string, contentMetadata?: ContentMetadata) => ContentNode
> = {
  [BuiltInComponents.Card]: (node: ContentNode, nodeId: string): ContentNode => {
    const eventName = `DismissedCard_${nodeId}`;
    return dismissLabelAdapter(eventName)(node);
  },
  // Checklist item
  [BuiltInComponents.CheckListItem]: (node: ContentNode): ContentNode => {
    if (node.type !== BuiltInComponents.CheckListItem) {
      return node;
    }
    const { stateChangeTrigger, ...otherCustom } = node?.data?.custom ?? {};
    if (!stateChangeTrigger) {
      return node;
    }
    const conditionals = {
      [CONDITIONAL_ID]: {
        // @ts-expect-error
        if: { id: `ds-${nanoid(6)}`, definition: hasEventOccurred(stateChangeTrigger?.eventName) },
        then: [{ type: ThenType.ChecklistItem }],
      },
    };
    const { states, ...otherNode } = node;
    return { ...otherNode, data: { ...node.data, custom: otherCustom, conditionals } };
  },
  // Forms
  [BuiltInComponents.FormTextInput]: formAdapter(CssSelectorId.FormTextInput),
  [BuiltInComponents.FormCheckboxList]: formAdapter(CssSelectorId.FormCheckboxListDiv),
  [BuiltInComponents.FormSelectInput]: formAdapter(CssSelectorId.FormSelectInput),
  [BuiltInComponents.FormTextarea]: formAdapter(CssSelectorId.FormTextArea),

  [BuiltInComponents.Modal]: modalAdapters,
};

export const documentNodeAdapters = (
  node: ContentNode,
  nodeId: string,
  contentMetadata?: ContentMetadata,
): ContentNode => {
  const adapter = adapters[node.type];
  if (!adapter) {
    return node;
  }
  return adapter(node, nodeId, contentMetadata);
};
